import React, { useState } from 'react';
import {
  Button,
  Flex,
  FlexItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { PlayIcon, PowerOffIcon, SyncAltIcon, RedoIcon } from '@patternfly/react-icons';
import { useVmPower } from '../../../hooks/useVmPower';
import { useNotifications } from '../../../contexts/NotificationContext';
import { VmPowerAction, VirtualMachine } from '../../../types/models/VirtualMachine';

interface VmPowerActionsProps {
  vm: VirtualMachine;
  powerState: string | null;
  onPowerActionSuccess: () => void;
}

const getButtonState = (powerState: string | null) => {
  switch (powerState) {
    case 'running':
      return { on: false, off: true, cycle: true, reset: true };
    case 'shutoff':
      return { on: true, off: false, cycle: false, reset: false };
    case 'paused':
      return { on: false, off: true, cycle: true, reset: true };
    case 'crashed':
      return { on: true, off: false, cycle: false, reset: false };
    case 'shutdown':
    default:
      return { on: false, off: false, cycle: false, reset: false };
  }
};

const getDisabledTooltip = (action: VmPowerAction, powerState: string | null): string => {
  if (!powerState) return 'Power state is unknown';
  switch (action) {
    case 'on':
      return powerState === 'running' ? 'VM is already running' : 'VM cannot be powered on in current state';
    case 'off':
      return powerState === 'shutoff' ? 'VM is already shut off' : 'VM cannot be powered off in current state';
    case 'cycle':
      return powerState === 'shutoff' ? 'VM is shut off' : 'VM cannot be power cycled in current state';
    case 'reset':
      return powerState === 'shutoff' ? 'VM is shut off' : 'VM cannot be reset in current state';
  }
};

const VmPowerActions: React.FunctionComponent<VmPowerActionsProps> = ({ vm, powerState, onPowerActionSuccess }) => {
  const { sendPowerAction, loading: powerLoading } = useVmPower();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [confirmAction, setConfirmAction] = useState<VmPowerAction | null>(null);
  const [activeAction, setActiveAction] = useState<VmPowerAction | null>(null);

  const allowed = getButtonState(powerState);

  const handlePowerAction = async (action: VmPowerAction) => {
    if (!vm.id) return;
    setActiveAction(action);
    try {
      const result = await sendPowerAction(vm.id, action);
      if (result.result === 'accepted') {
        addNotification({
          title: 'Power action sent',
          srTitle: 'Success alert',
          description: `Power ${action} command sent to ${vm.name}.`,
          variant: 'success',
        });
      } else {
        addNotification({
          title: 'Power action response',
          srTitle: 'Warning alert',
          description: `Power ${action} command returned: ${result.result}`,
          variant: 'warning',
        });
      }
      setDrawerOpen(true);
      onPowerActionSuccess();
    } catch (err) {
      addNotification({
        title: 'Power action failed',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setActiveAction(null);
      setConfirmAction(null);
    }
  };

  const onPowerButtonClick = (action: VmPowerAction) => {
    setConfirmAction(action);
  };

  const renderPowerButton = (
    action: VmPowerAction,
    variant: 'primary' | 'danger' | 'warning',
    icon: React.ReactNode,
    label: string,
    isAllowed: boolean,
  ) => {
    const isStatusDisabled = !isAllowed;
    const button = (
      <Button
        variant={variant}
        icon={activeAction === action ? <Spinner size="sm" /> : icon}
        isDisabled={powerLoading}
        isAriaDisabled={!powerLoading && isStatusDisabled}
        onClick={() => onPowerButtonClick(action)}
      >
        {label}
      </Button>
    );

    if (isStatusDisabled && !powerLoading) {
      return <Tooltip content={getDisabledTooltip(action, powerState)}>{button}</Tooltip>;
    }
    return button;
  };

  const getConfirmVariant = (action: VmPowerAction): 'primary' | 'danger' | 'warning' => {
    switch (action) {
      case 'on':
        return 'primary';
      case 'off':
        return 'danger';
      case 'cycle':
      case 'reset':
        return 'warning';
    }
  };

  return (
    <>
      <Flex spaceItems={{ default: 'spaceItemsSm' }}>
        <FlexItem>{renderPowerButton('on', 'primary', <PlayIcon />, 'Power On', allowed.on)}</FlexItem>
        <FlexItem>{renderPowerButton('off', 'danger', <PowerOffIcon />, 'Power Off', allowed.off)}</FlexItem>
        <FlexItem>{renderPowerButton('cycle', 'warning', <SyncAltIcon />, 'Recycle Power', allowed.cycle)}</FlexItem>
        <FlexItem>{renderPowerButton('reset', 'warning', <RedoIcon />, 'Reset', allowed.reset)}</FlexItem>
      </Flex>

      {confirmAction && (
        <Modal
          variant={ModalVariant.small}
          isOpen={!!confirmAction}
          onClose={() => setConfirmAction(null)}
          aria-label="Confirm power action"
        >
          <ModalHeader title={`Confirm power ${confirmAction}`} titleIconVariant="warning" />
          <ModalBody>
            Are you sure you want to power {confirmAction} <strong>{vm.name}</strong>?
          </ModalBody>
          <ModalFooter>
            <Button
              variant={getConfirmVariant(confirmAction)}
              onClick={() => handlePowerAction(confirmAction)}
              isLoading={powerLoading}
              isDisabled={powerLoading}
            >
              Confirm
            </Button>
            <Button variant="link" onClick={() => setConfirmAction(null)} isDisabled={powerLoading}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default VmPowerActions;
